import { useState, useEffect, useRef } from 'react';
import useUserPreferences from './useUserPreferences';

interface UseIncludeNewBuyerStateReturn {
  autoCreateOffersForNewBuyers: boolean | null | undefined;
  isChecked: boolean;
  isEnrolled: boolean;
  setIsChecked: (checked: boolean) => void;
  shouldSubmitUpdate: (currentChecked: boolean) => boolean;
}

export const useIncludeNewBuyerState = (options?: { enabled?: boolean }): UseIncludeNewBuyerStateReturn => {
  const hasInitializedRef = useRef(false);
  const [isChecked, setIsChecked] = useState(false);
  const { data: userPreferences } = useUserPreferences(undefined, options);
  const autoCreateOffersForNewBuyers = userPreferences?.autoCreateOffersForNewBuyers;

  const isEnrolled = autoCreateOffersForNewBuyers === true;

  useEffect(() => {
    if (!hasInitializedRef.current && autoCreateOffersForNewBuyers !== undefined) {
      hasInitializedRef.current = true;
      // only need to set checkbox to true if user preference is null
      // if false, leave it as false (default state)
      // if true, user is enrolled so checkbox isn't shown
      if (autoCreateOffersForNewBuyers === null) {
        setIsChecked(true);
      }
    }
  }, [autoCreateOffersForNewBuyers]);

  const shouldSubmitUpdate = (currentChecked: boolean) => {
    // no submit if user is already enrolled
    if (isEnrolled) {
      return false;
    }

    // submit if:
    // - api returned null
    // - api returned false and user checked the box
    return autoCreateOffersForNewBuyers === null || (autoCreateOffersForNewBuyers === false && currentChecked);
  };

  return {
    autoCreateOffersForNewBuyers,
    isEnrolled,
    isChecked,
    setIsChecked,
    shouldSubmitUpdate,
  };
};

export default useIncludeNewBuyerState;
